import React from 'react';
import { Loader } from 'freemium-ui';
import { useCombobox } from 'downshift';
import styled from 'styled-components';
import { ErrorField } from 'shared/styles';
import {
  DropdownContainer,
  ItemsContainer,
  ListItem,
  LoaderContainer,
  SelectContent,
  SelectGroup,
  SelectIcon,
  SelectItem,
  SelectItemText,
  SelectLabel,
  SelectLabelDivider,
  SelectPortal,
  SelectRoot,
  SelectScrollUpButton,
  SelectTrigger,
  SelectValue,
  SelectViewport,
  StyledSelect,
  TextAddButton,
} from './styles';
import ChevronUp from '../Chevron';
import {
  DefaultDropdownItem,
  DropdownButtonProps,
  DropdownInputItemProps,
  DropdownInputProps,
  DropdownItemProps,
  DropdownProps,
} from './Dropdown.types';

/**
 * Dropdown displays a list or drop-down box that enables selection of an option or multiple options from an available list of values.
 */
export const DropdownV2 = <T extends DefaultDropdownItem>({
  type = 'button',
  ...props
}: DropdownProps<T>) => {
  return type === 'button' ? (
    <DropdownButton {...props} type="button" />
  ) : (
    <DropdownInput {...props} type="input" />
  );
};

const RequiredIndicator = styled.div`
  color: red;
  margin-left: 2px;
`;

const StyledLabel = styled.div`
  color: black;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  display: flex;
  margin-bottom: 4px;
`;

const DropdownButton = <T extends DefaultDropdownItem>(
  props: DropdownButtonProps<T>,
) => {
  const {
    itemIdentifier = 'id',
    label,
    id,
    items = [],
    onChange,
    placeholder,
    readOnly = false,
    disabled = false,
    isRequired = false,
    showTick = true,
    itemToString,
    name,
    itemComponent,
    width,
    className,
    style,
    defaultSelectedItem = null,
    isErrored,
    isMenuLoading,
    errorText,
    defaultIsOpen,
    isOpen,
  } = props;

  const groupedItems = React.useMemo(() => {
    const grouped = items.reduce((acc, item) => {
      const key = item.groupBy ?? 'default';
      if (acc[key]) {
        acc[key].push(item);
      } else {
        acc[key] = [item];
      }
      return acc;
    }, {} as Record<string, T[]>);
    return Object.keys(grouped)
      .sort((a, b) => (a !== 'default' ? 1 : -1))
      .map((key) => ({
        key,
        items: grouped[key],
      }));
  }, [items]);

  return (
    <StyledSelect>
      {label && (
        <>
          <StyledLabel className="input-label" aria-label={label} htmlFor={id}>
            {label}
            {isRequired && <RequiredIndicator>*</RequiredIndicator>}
          </StyledLabel>
        </>
      )}
      <SelectRoot
        open={isOpen}
        defaultOpen={defaultIsOpen}
        name={name}
        required={isRequired}
        disabled={disabled}
        value={defaultSelectedItem?.[itemIdentifier] ?? undefined}
        onValueChange={(val) => {
          if (readOnly) return;
          const selectedItem = items.find(
            (item) => item?.[itemIdentifier] === val,
          ) as T;
          onChange?.(selectedItem ?? null);
        }}
      >
        <SelectTrigger
          className={`SelectTrigger ${className || ''}`.trim()}
          aria-label={label}
          disabled={disabled}
          id={id}
          isErrored={!!isErrored}
          aria-invalid={!!isErrored}
          width={width}
          style={style}
        >
          <SelectValue className="SelectValue" placeholder={placeholder} />
          <SelectIcon className="SelectIcon">
            <ChevronUp fill={'#12344d'} />
          </SelectIcon>
        </SelectTrigger>
        <SelectPortal>
          <SelectContent
            className="SelectContent"
            position="popper"
            side="bottom"
            sideOffset={4}
            sticky="partial"
          >
            {isMenuLoading ? (
              <LoaderContainer>
                <Loader />
              </LoaderContainer>
            ) : (
              <>
                <SelectScrollUpButton className="SelectScrollButton">
                  <ChevronUp fill={'#12344d'} />
                </SelectScrollUpButton>
                <SelectViewport className="SelectViewport">
                  {groupedItems.map((group) => {
                    return (
                      <SelectGroup key={group.key} className="SelectGroup">
                        {group.key !== 'default' && (
                          <SelectLabel className="SelectLabel">
                            {group.key}
                            <SelectLabelDivider />
                          </SelectLabel>
                        )}
                        {group.items.map((item, index) => {
                          const key =
                            typeof item === 'string'
                              ? item
                              : ((item as any)?.[itemIdentifier] as string);
                          const value = item?.[itemIdentifier] ?? '';
                          if (!value || value === '')
                            console.warn('No value for item', item);
                          return (
                            <>
                              {itemComponent ? (
                                itemComponent(item, {
                                  key,
                                  value: value || key.trim,
                                  disabled: !!item.disabled,
                                  showTick,
                                } as DropdownItemProps)
                              ) : (
                                <Item
                                  key={key}
                                  value={value || key}
                                  disabled={!!item.disabled}
                                  showTick={showTick}
                                >
                                  {itemToString(
                                    item,
                                    group.key != 'default'
                                      ? group.key
                                      : undefined,
                                    'list-item',
                                  )}
                                </Item>
                              )}
                            </>
                          );
                        })}
                      </SelectGroup>
                    );
                  })}
                </SelectViewport>
              </>
            )}
          </SelectContent>
        </SelectPortal>
      </SelectRoot>
      {isErrored && errorText && <ErrorField>{errorText}</ErrorField>}
    </StyledSelect>
  );
};

const DropdownInput = <T extends DefaultDropdownItem>({
  itemIdentifier = 'id',
  label,
  items = [],
  readOnly = false,
  disabled = false,
  isRequired = false,
  itemToString,
  filterKey,
  width,
  customValues,
  onCustomValueAdd,
  itemComponent,
  emptyItemsText,
  getCustomMessage,
  onChange,
  useComboboxProps,
  defaultSelectedItem,
  isMenuLoading,
}: DropdownInputProps<T>) => {
  const [localItems, setLocalItems] = React.useState(items);
  const {
    isOpen,
    selectedItem,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    setHighlightedIndex,
    setInputValue,
    inputValue,
    getItemProps,
  } = useCombobox({
    selectedItem: defaultSelectedItem,
    items: localItems,
    onSelectedItemChange: ({ selectedItem: sI }) => {
      onChange?.(sI ?? null);
    },
    onIsOpenChange: ({ isOpen: iO }) => {
      if (
        iO &&
        (inputValue === '' ||
          (!!selectedItem &&
            !!filterKey &&
            (inputValue || '').toLowerCase() ===
              `${
                (selectedItem as any)?.[filterKey || itemIdentifier] || ''
              }`.toLowerCase()))
      ) {
        setLocalItems(items);
        setHighlightedIndex(selectedItem ? items.indexOf(selectedItem) : -1);
      } else {
        const iToS = selectedItem ? itemToString(selectedItem) : '';
        if (selectedItem && !iO && iToS !== inputValue) {
          setInputValue(iToS);
        }
      }
      if (!iO) {
        setLocalItems(items);
      }
    },
    onInputValueChange: ({ inputValue }) => {
      if (
        isOpen &&
        (inputValue === '' ||
          (!!selectedItem &&
            !!filterKey &&
            (inputValue || '').toLowerCase() ===
              `${
                (selectedItem as any)?.[filterKey || itemIdentifier] || ''
              }`.toLowerCase()))
      ) {
        setLocalItems(items);
      } else {
        setLocalItems((oldItems) =>
          oldItems.filter((i) => {
            const key =
              typeof i === 'string'
                ? i
                : ((i as any)?.[filterKey || itemIdentifier] as string);
            return inputValue
              ? key.toLowerCase().includes(inputValue.toLowerCase())
              : true;
          }),
        );
      }
    },
    itemToString: (item) => {
      return item ? itemToString(item, 'downshift') : '';
    },
    ...useComboboxProps,
  });
  const isOpenFlag = !disabled && !readOnly && isOpen;
  return (
    <DropdownContainer width={width} isOpen={isOpenFlag}>
      <StyledLabel
        className="input-label"
        {...getLabelProps({ disabled, readOnly })}
      >
        {label}
        {isRequired && <RequiredIndicator>*</RequiredIndicator>}
      </StyledLabel>
      {!readOnly && !disabled && (
        <ItemsContainer
          isOpen={isOpen}
          {...getMenuProps({ disabled, readOnly })}
        >
          {isMenuLoading ? (
            <LoaderContainer>
              <Loader />
            </LoaderContainer>
          ) : (
            <>
              {isOpen && localItems.length === 0 && (
                <>
                  {customValues ? (
                    <ListItem>
                      <TextAddButton
                        onClick={(event: React.MouseEvent<HTMLElement>) => {
                          event.stopPropagation();
                          if (onCustomValueAdd)
                            onCustomValueAdd({
                              inputValue,
                              customValues: true,
                            });
                          setInputValue('');
                        }}
                      >
                        {getCustomMessage?.(inputValue) || 'Click to add'}
                      </TextAddButton>
                    </ListItem>
                  ) : (
                    <ListItem readOnly>{emptyItemsText || 'No items'}</ListItem>
                  )}
                </>
              )}
              {isOpen &&
                localItems.map((item, index) => {
                  const key =
                    typeof item === 'string'
                      ? item
                      : ((item as any)?.[itemIdentifier] as string);
                  return (
                    <>
                      {itemComponent ? (
                        itemComponent(item, {
                          isHilighted: highlightedIndex === index,
                          isSelected: selectedItem === item,
                          key,
                          ...getItemProps({ item, index }),
                        } as DropdownInputItemProps)
                      ) : (
                        <ListItem
                          isHilighted={highlightedIndex === index}
                          isSelected={selectedItem === item}
                          key={key}
                          {...getItemProps({ item, index })}
                        >
                          {itemToString(item)}
                        </ListItem>
                      )}
                    </>
                  );
                })}
            </>
          )}
        </ItemsContainer>
      )}
    </DropdownContainer>
  );
};

const Item = React.forwardRef<HTMLDivElement, DropdownItemProps>(
  // eslint-disable-next-line react/prop-types
  ({ children, ...props }, forwardedRef) => {
    return (
      <SelectItem className="SelectItem" {...props} ref={forwardedRef}>
        <SelectItemText className="SelectItemText">{children}</SelectItemText>
      </SelectItem>
    );
  },
);

Item.displayName = 'Item';

export default DropdownV2;
